import { MIDDLE, grayFromRgba, sharpness } from '../spores/focus';

/*
 * What one small video frame says, in numbers: the same pure code on the
 * frame worker (frame-worker.js) and, where there is no worker, on the main
 * thread (components/camera/useFrameSampler.js).
 *
 *   sharpness   the focus meter's own number (utils/spores/focus.js).
 *   motion      how far the picture moved since the frame before: the mean
 *               absolute difference of the grey levels, 0-255, between two
 *               thumbnails (THUMB_BIN x THUMB_BIN pixels to one, so the camera's
 *               grain counts for little); null on the first frame.
 *   mean        the mean grey level, 0-255, over the middle of the picture.
 *   dark        the share of the middle at or under DARK_LEVEL, 0-1.
 *   bright      the share of the middle at or over BRIGHT_LEVEL, 0-1.
 *
 * Exposure reads only the middle because an eyepiece's dark surround would
 * otherwise read as an underexposed picture on every microscope view.
 */
export const THUMB_BIN = 8;
export const DARK_LEVEL = 16;
export const BRIGHT_LEVEL = 240;

/** The picture binned THUMB_BIN x THUMB_BIN: {values, w, h}. A picture smaller than a bin has an empty one. */
export function thumbnail(gray, w, h) {
  const tw = Math.floor(w / THUMB_BIN);
  const th = Math.floor(h / THUMB_BIN);
  const values = new Float32Array(Math.max(0, tw * th));
  for (let ty = 0; ty < th; ty += 1) {
    for (let tx = 0; tx < tw; tx += 1) {
      let sum = 0;
      for (let y = ty * THUMB_BIN; y < (ty + 1) * THUMB_BIN; y += 1) {
        for (let x = tx * THUMB_BIN; x < (tx + 1) * THUMB_BIN; x += 1) sum += gray[y * w + x];
      }
      values[ty * tw + tx] = sum / (THUMB_BIN * THUMB_BIN);
    }
  }
  return { values, w: tw, h: th };
}

/** The mean absolute difference between two thumbnails; null without a comparable earlier one. */
export function motionBetween(prev, thumb) {
  if (!prev || prev.w !== thumb.w || prev.h !== thumb.h || !thumb.values.length) return null;
  let sum = 0;
  for (let i = 0; i < thumb.values.length; i += 1) sum += Math.abs(thumb.values[i] - prev.values[i]);
  return sum / thumb.values.length;
}

/** {mean, dark, bright} over the middle of a thumbnail; all null when it is empty. */
export function exposureOf(thumb) {
  const x0 = Math.floor((thumb.w * (1 - MIDDLE)) / 2);
  const x1 = Math.ceil((thumb.w * (1 + MIDDLE)) / 2);
  const y0 = Math.floor((thumb.h * (1 - MIDDLE)) / 2);
  const y1 = Math.ceil((thumb.h * (1 + MIDDLE)) / 2);
  let sum = 0;
  let dark = 0;
  let bright = 0;
  let n = 0;
  for (let y = y0; y < y1; y += 1) {
    for (let x = x0; x < x1; x += 1) {
      const v = thumb.values[y * thumb.w + x];
      sum += v;
      if (v <= DARK_LEVEL) dark += 1;
      if (v >= BRIGHT_LEVEL) bright += 1;
      n += 1;
    }
  }
  if (!n) return { mean: null, dark: null, bright: null };
  return { mean: sum / n, dark: dark / n, bright: bright / n };
}

/** One frame's numbers, and the thumbnail to hand to the next call as `prevThumb`. */
export function analyseGray(gray, w, h, prevThumb = null) {
  const thumb = thumbnail(gray, w, h);
  return {
    reading: { sharpness: sharpness(gray, w, h), motion: motionBetween(prevThumb, thumb), ...exposureOf(thumb) },
    thumb,
  };
}

/** As analyseGray, for the RGBA of a canvas. */
export function analyseRgba(rgba, w, h, prevThumb = null) {
  return analyseGray(grayFromRgba(rgba, w, h), w, h, prevThumb);
}
